<template>
  <Layout v-if="wait.is('Loading Docs')" :showUpgradePromptModal="false">
    <div class="layout__wrapper">
      <loading-spinner />
    </div>
  </Layout>

  <!-- For experiment 20321 variant B, LibraryHome20321 replaces the
    top-level "my_library.books" route. Other routes still go through the
    router-view. -->
  <LibraryHome20321
    v-else-if="showNewLibrary20321 && docsUserData && docs && lists"
    :docsUserData="docsUserData"
    :docs="docs"
    :lists="lists"
  />

  <router-view
    v-else-if="docsUserData && docs && lists"
    :docsUserData="docsUserData"
    :docs="docs"
    :lists="lists"
  ></router-view>

  <Layout
    v-else-if="
      (!docsUserData || !docs || !lists) && wait.is('Error Loading Docs')
    "
    :showUpgradePromptModal="false"
  >
    <div class="layout__wrapper">
      <LoadingError />
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { onBeforeRouteUpdate, useRouter } from 'vue-router'
import { backend } from '@/services/backend'
import type {
  CustomListWithDocs,
  Doc,
  DocsUserDataResponse,
} from '@/models/interfaces'

import Layout from '@/app/components/Layout.vue'
import LibraryHome20321 from '@/app/components/library/LibraryHome20321.vue'

import { initializeFirstLaunchList } from '@/models/onboarding'
import { saveForLater20321Treatment } from '@/services/ab/experiments'

const docsUserData = ref<DocsUserDataResponse | null>(null)
const docs = ref<Doc[] | null>(null)
const lists = ref<CustomListWithDocs[] | null>(null)
const wait = useWait()
const router = useRouter()
let initialDataLoad: Promise<void> | null = null

const showNewLibrary20321 = computed(() => {
  return (
    saveForLater20321Treatment() &&
    router.currentRoute.value.name === 'my_library.books'
  )
})

onBeforeMount(() => {
  initialDataLoad = loadDocuments()
})

onBeforeRouteUpdate(async (to) => {
  // This reloads lists when switching from a different My Library tab to
  // Lists. That first tab could have mutated the lists (e.g. from adding to a
  // new list using a book card's menu), so the reload is necessary to update
  // the list data.
  if (to.name === 'my_library.custom_lists') {
    // Wait for the loadDocuments() from the earlier tab to finish, so that
    // the old list data does not overwrite the reloaded lists.
    // Addresses #19366.
    if (initialDataLoad) {
      await initialDataLoad
    }
    try {
      lists.value = await backend.getCustomLists()
    } catch (error) {
      Sentry?.captureException(error)
    }
  }
})

const loadDocuments = async (): Promise<void> => {
  try {
    wait.start('Loading Docs')
    wait.end('Error Loading Docs')

    // Initialize Want to Read before loading, so that the seeded docs are
    // shown on first load.
    await initializeFirstLaunchList()

    const [userData, docsData, listsData] = await Promise.all([
      backend.getDocsUserData(),
      backend.getUserDocs(),
      backend.getCustomLists(),
    ])

    docsUserData.value = userData
    docs.value = docsData
    lists.value = listsData
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      router.push({ name: 'not_found' })
    } else {
      wait.start('Error Loading Docs')
      if (Sentry) {
        Sentry.captureException(error)
      }
    }
  } finally {
    wait.end('Loading Docs')
  }
}
</script>
